import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import type { Notification } from '../features/dashboard/dashboardSlice';
import { RootState } from '../reducers';

/**
 * Hook that automatically displays toast notifications when new notifications
 * are added to Redux state. This replaces the sidebar notification dropdown
 * with elegant, non-intrusive toast messages.
 */
export const useNotificationToasts = () => {
  const { notifications } = useSelector((state: RootState) => state.dashboard);
  const previousNotificationsRef = useRef<Notification[]>([]);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const previousNotifications = previousNotificationsRef.current;

    // On initial load, just store the notifications without showing toasts
    if (isInitialLoadRef.current) {
      previousNotificationsRef.current = notifications;
      isInitialLoadRef.current = false;
      return;
    }

    // Find new notifications that weren't in the previous state
    const newNotifications = notifications.filter(
      (notification) =>
        !previousNotifications.some((prev) => prev.id === notification.id),
    );

    // Show toast for each new notification
    newNotifications.forEach((notification) => {
      // Skip showing toast for notifications that are already read
      if (notification.read) {
        return;
      }

      const { link } = notification;

      // Use appropriate Sonner variant based on notification type
      const toastFn = notification.type === 'error' ? toast.error : toast;

      toastFn(notification.title, {
        description: notification.message,
        duration: 5000, // Auto-dismiss after 5 seconds
        action: link
          ? {
              label: 'View',
              onClick: () => {
                window.location.href = link;
              },
            }
          : undefined,
      });
    });

    // Update the ref with current notifications
    previousNotificationsRef.current = notifications;
  }, [notifications]);
};
